<template>
  <div class="n3-advanced-stats" v-if="availableN3">
    <div class="dashboard-panels">
      <div class="dashboard-panels--tabs">
        <div>
          <!-- Header and Selectors -->
          <div class="d-flex justify-space-between align-center">
            <SectionHeader class="pb-0" first="Estadísticas" second="N3/Anticipos" />
          </div>
          <div class="dashboard-toolbar--selector pb-3 d-flex mt-4">
            <div class="dashboard-toolbar--field-group mr-5">
              <div class="dashboard-toolbar--field-group__label work-center">
                <v-icon small>mdi-office-building-marker</v-icon>
                Centro de trabajo:
              </div>
              <div class="dashboard-toolbar--field-group__value">
                <div class="dashboard-toolbar--field-group__value__text" v-if="workcenterId !== null">
                  <v-select style="min-width: 400px;" v-model="selectedWorkcenterId" :items="workcenters"
                    :placeholder="workcenterName ? workcenterName : 'Seleccionar Centro de Trabajo'" outlined dense
                    hide-details @change="loadStats()">
                  </v-select>
                </div>
              </div>
            </div>
            <div class="dashboard-toolbar--field-group">
              <div class="dashboard-toolbar--field-group__label work-center">
                <v-icon small>mdi-calendar</v-icon>
                Año:
              </div>
              <div class="dashboard-toolbar--field-group__value">
                <div class="dashboard-toolbar--field-group__value__text">
                  <v-select v-model="selectedYear" :items="years"
                    :placeholder="selectedYear ? selectedYear.toString() : 'Seleccionar Año'" outlined dense
                    hide-details @change="loadStats()"></v-select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <v-divider class="mb-4"></v-divider>

        <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 300px;">
          <v-progress-circular indeterminate color="primary" size="56"></v-progress-circular>
        </div>

        <div v-else-if="!statsData" class="d-flex flex-column align-center justify-center" style="min-height: 300px;">
          <v-icon size="72" class="text--disabled">mdi-chart-areaspline</v-icon>
          <p class="text--secondary mt-3">Selecciona un centro y año para ver las estadísticas</p>
        </div>

        <div v-else class="px-4">
          <v-row class="mb-4">
            <!-- N3 Chart -->
            <v-col cols="12" md="8">
              <v-card class="elevation-1 pa-4 h-100 rounded-lg">
                <h3 class="text-subtitle-1 font-weight-bold mb-4">
                  <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
                  Evolución Anual ({{ selectedYear }})
                </h3>
                <div style="height: 300px; position: relative;">
                  <N3Chart ref="n3Chart" :chart-data="temporalData" />
                </div>
              </v-card>
            </v-col>

            <!-- N3 KPIs -->
            <v-col cols="12" md="4">
              <v-row class="ma-n2">
                <v-col cols="12" sm="6" md="12" class="pa-2">
                  <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100">
                    <v-icon large color="info" class="mr-3">mdi-cash-multiple</v-icon>
                    <div>
                      <div class="text-subtitle-2 text--secondary">Total Importe</div>
                      <div class="text-h5 font-weight-bold">{{ statsData.totalAmount }} €</div>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="12" class="pa-2">
                  <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100">
                    <v-icon large color="success" class="mr-3">mdi-calculator</v-icon>
                    <div>
                      <div class="text-subtitle-2 text--secondary">Promedio / Solicitud</div>
                      <div class="text-h5 font-weight-bold">{{ avgAmountPerRequest }} €</div>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="12" class="pa-2">
                  <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100">
                    <v-icon large color="secondary" class="mr-3">mdi-file-document-outline</v-icon>
                    <div>
                      <div class="text-subtitle-2 text--secondary">Total Solicitudes</div>
                      <div class="text-h5 font-weight-bold">{{ statsData.totalRequests }}</div>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="12" class="pa-2">
                  <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100">
                    <v-icon large color="primary" class="mr-3">mdi-check-circle-outline</v-icon>
                    <div>
                      <div class="text-subtitle-2 text--secondary">Aprobaciones / Pendientes</div>
                      <div class="text-h5 font-weight-bold">{{ statsData.approvedCount }} / {{ statsData.pendingCount }}</div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Resumen por meses -->
          <v-card elevation="1" class="mt-6 mb-6">
            <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
              <v-icon color="primary" class="mr-2">mdi-calendar-multiselect</v-icon>
              Desglose Mensual
            </v-card-title>
            <v-divider></v-divider>
            <v-simple-table>
              <thead>
                <tr>
                  <th>Mes</th>
                  <th>Solicitudes</th>
                  <th>Importe Total</th>
                  <th>Aprobadas / Pendientes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in temporalData" :key="d.monthNumber">
                  <td>
                    <div class="font-weight-medium text-capitalize">{{ d.monthName.toLowerCase() }}</div>
                  </td>
                  <td>{{ d.totalCount }}</td>
                  <td class="font-weight-bold">{{ d.totalAmount }} €</td>
                  <td>
                    <v-chip x-small color="success" dark class="mr-1" v-if="d.approvedCount > 0">
                      {{ d.approvedCount }}
                    </v-chip>
                    <v-chip x-small color="warning" dark v-if="d.pendingCount > 0">
                      {{ d.pendingCount }}
                    </v-chip>
                    <span v-if="d.totalCount === 0" class="text--disabled">-</span>
                  </td>
                </tr>
                <tr v-if="temporalData.length === 0">
                  <td colspan="4" class="text-center text--secondary py-4">No hay datos en el periodo.</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-cycle
import n3Service from '@/services/n3.service';
import SectionHeader from '@/components/Headers/SectionHeader.vue';
import workcenterService from '@/services/workcenter.service';
import N3Chart from '@/components/Charts/N3Chart.vue';

export default {
  name: 'n3-advanced-stats',
  components: {
    SectionHeader,
    N3Chart,
  },
  data() {
    return {
      loading: false,

      workcenterId: null,
      workcenterName: null,

      years: [2025, 2026],
      currentYear: new Date().getFullYear(),
      selectedYear: null,

      workcenters: null,
      selectedWorkcenterId: null,

      statsData: null,
      temporalData: [],
    };
  },
  computed: {
    availableN3() {
      return this.$store.state.availableN3 === 'true';
    },
    avgAmountPerRequest() {
      if (!this.statsData || this.statsData.totalRequests === 0) return '0.00';
      return (parseFloat(this.statsData.totalAmount) / this.statsData.totalRequests).toFixed(2);
    },
  },
  created() {
    this.getWorkcenters();
  },
  mounted() {
    if (this.$store.state.projectId) {
      this.workcenterId = this.$store.state.projectId;
      this.workcenterName = this.$store.state.project;
      this.selectedWorkcenterId = this.workcenterId;
    }
    this.selectedYear = this.currentYear;
    this.loadStats();
  },
  methods: {
    getWorkcenters() {
      workcenterService.getAllWorkcenters().then((response) => {
        this.workcenters = response.content.map((ele) => ({
          value: ele.id,
          text: ele.name,
        }));
      });
    },
    async loadStats() {
      if (!this.selectedWorkcenterId || !this.selectedYear) return;
      this.loading = true;
      this.statsData = null;
      this.temporalData = [];

      try {
        const response = await n3Service.getWorkersN3EntriesInRange(this.selectedWorkcenterId, this.selectedYear);
        const entries = response || [];

        let totalAmount = 0;
        const totalRequests = entries.length;
        let approvedCount = 0;
        let pendingCount = 0;

        // Initialize temporal grouped by month for all 12 months
        const monthsData = {};
        const monthNames = [
          'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
          'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE',
        ];

        for (let i = 0; i < 12; i += 1) {
          const monthNumber = i + 1;
          monthsData[monthNumber] = {
            monthNumber,
            monthName: monthNames[i],
            totalAmount: 0,
            totalCount: 0,
            approvedCount: 0,
            pendingCount: 0,
          };
        }

        entries.forEach((item) => {
          const amount = parseFloat(item.amount || 0);
          totalAmount += amount;
          if (item.approved) {
            approvedCount += 1;
          } else {
            pendingCount += 1;
          }

          if (monthsData[item.month]) {
            monthsData[item.month].totalAmount += amount;
            monthsData[item.month].totalCount += 1;
            if (item.approved) {
              monthsData[item.month].approvedCount += 1;
            } else {
              monthsData[item.month].pendingCount += 1;
            }
          }
        });

        this.statsData = {
          totalAmount: totalAmount.toFixed(2),
          totalRequests,
          approvedCount,
          pendingCount,
        };

        // Convert the map to an array and sort by month
        this.temporalData = Object.values(monthsData).sort((a, b) => a.monthNumber - b.monthNumber);

        // Format decimal values
        this.temporalData = this.temporalData.map((d) => ({
          ...d,
          totalAmount: d.totalAmount.toFixed(2),
        }));

        this.$nextTick(() => {
          if (this.$refs.n3Chart) {
            this.$refs.n3Chart.updateChartData(this.temporalData);
          }
        });
      } catch (e) {
        console.error('Error fetching N3 stats:', e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.n3-advanced-stats {
  width: 100%;
}

.dashboard-toolbar--selector {
  display: flex;
  align-items: center;
}

.dashboard-toolbar--field-group {
  display: flex;
  flex-direction: column;
}

.dashboard-toolbar--field-group__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
}
</style>
